import { Tabs, type TabsProps } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import './CustomerLeadStepContacts.scss';

import { CustomerLeadStepAddressTab } from 'src/components/customers/steps/Contact/CustomerLeadStepAddressTab';
import { CustomerLeadStepContactTab } from 'src/components/customers/steps/Contact/CustomerLeadStepContactTab';
import { CustomerLeadStepSalesTaxInfoTab } from 'src/components/customers/steps/Contact/CustomerLeadStepSalesTaxInfoTab';
import { CustomerLeadStepLayout } from 'src/components/customers/steps/CustomerLeadStepLayout';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

const CustomerLeadStepContactSubSteps: Partial<Record<string, string>> = {
  [ProductSpecificationFieldGroup.Contacts]: ProductSpecificationFieldGroup.ShippingAddress,
  [ProductSpecificationFieldGroup.ShippingAddress]: ProductSpecificationFieldGroup.BillingAddress,
  [ProductSpecificationFieldGroup.BillingAddress]: ProductSpecificationFieldGroup.SalesTaxInfo,
};

type CustomerLeadStepContactProps = {
  customerProductLeadId: string;
};

export function CustomerLeadStepContacts({ customerProductLeadId }: Readonly<CustomerLeadStepContactProps>) {
  const [currentTab, setCurrentTab] = useState<string>(ProductSpecificationFieldGroup.Contacts);

  const { isEditing, setCustomerStepGuideKey, setLastCustomerStep } = useContext(CustomersContext);

  const handleSubmit = useCallback(() => {
    const newSubStep = CustomerLeadStepContactSubSteps[currentTab];

    const moveToNextStep = newSubStep === undefined;

    if (!moveToNextStep) {
      setCurrentTab(newSubStep);
    }

    // Go to next step if empty
    return moveToNextStep;
  }, [currentTab]);

  const tabs: Exclude<TabsProps['items'], undefined> = useMemo(
    () => [
      {
        key: ProductSpecificationFieldGroup.Contacts,
        label: 'Contact',
        disabled: isEditing,
        children: <CustomerLeadStepContactTab customerProductLeadId={customerProductLeadId} onSubmit={handleSubmit} />,
      },
      {
        key: ProductSpecificationFieldGroup.ShippingAddress,
        label: 'Shipping Address',
        disabled: isEditing,
        children: (
          <CustomerLeadStepAddressTab
            customerProductLeadId={customerProductLeadId}
            addressFieldName="shippingAddress"
            onSubmit={handleSubmit}
          />
        ),
      },
      {
        key: ProductSpecificationFieldGroup.BillingAddress,
        label: 'Billing Address',
        disabled: isEditing,
        children: (
          <CustomerLeadStepAddressTab
            customerProductLeadId={customerProductLeadId}
            addressFieldName="billingAddress"
            onSubmit={handleSubmit}
          />
        ),
      },
      {
        key: ProductSpecificationFieldGroup.SalesTaxInfo,
        label: 'Sales Tax Info',
        disabled: isEditing,
        children: (
          <CustomerLeadStepSalesTaxInfoTab customerProductLeadId={customerProductLeadId} onSubmit={handleSubmit} />
        ),
      },
    ],
    [isEditing, customerProductLeadId, handleSubmit]
  );

  const handleTabChange = useCallback((newActiveKey: string) => {
    setCurrentTab(newActiveKey as ProductSpecificationFieldGroup);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    setCustomerStepGuideKey(
      (
        [
          ProductSpecificationFieldGroup.Contacts,
          ProductSpecificationFieldGroup.ShippingAddress,
          ProductSpecificationFieldGroup.BillingAddress,
          ProductSpecificationFieldGroup.SalesTaxInfo,
        ] as string[]
      ).includes(currentTab)
        ? currentTab
        : null
    );

    setLastCustomerStep(currentTab === ProductSpecificationFieldGroup.SalesTaxInfo);
  }, [currentTab]);

  return (
    <CustomerLeadStepLayout>
      <div className="customer-lead-step-contacts__container">
        <Tabs
          items={tabs}
          activeKey={currentTab}
          animated={false}
          destroyInactiveTabPane={false}
          onChange={handleTabChange}
        />
      </div>
    </CustomerLeadStepLayout>
  );
}
